import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, HostBinding } from '@angular/core';
import { ImpersonateDialogComponent } from './impersonate-dialog/impersonate-dialog.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { get } from 'lodash';
import { AuthService, AuthSystemRole, AuthUserRole } from 'providers/auth.service';
import { PopupService } from 'shared/popup-service/popup.service';
import { CustomerPrivacyService } from 'layout/customer-privacy/customer-privacy.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GdprPrivacyService } from 'shared/gdpr-privacy/gdpr-privacy.service';
import { toTitleCase } from 'shared/utils/toTitleCase';
import { translateSystemRole } from 'shared/utils/translateSystemRole';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { PANEL_LOCALES } from '@kempowersw/charger-config';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { LocaleService } from 'shared/locale/locale.service';
import { getCurrentLanguageFromPath } from 'shared/utils/getCurrentLanguageFromPath';
import { filter, map } from 'rxjs/operators';
import { CookieDialogComponent } from './cookie-dialog/cookie-dialog.component';
import { environment } from '../../../../environments/environment';
import { EnvironmentService } from 'providers/environment.service';
import { FeatureFlagService, DEVELOPMENT_FEATURE_FLAGS } from '../../../services/feature-flags';

interface ImpersonatedAs {
    userRole: AuthUserRole;
    systemRoles?: AuthSystemRole[];
    systemRolesString?: string;
}
@Component({
    selector: 'ce-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class ProfileComponent {
    @Input() @HostBinding('class') public themeClass: 'theme-mykempower' | 'theme-chargeye' = 'theme-chargeye';

    // Use PANEL_LOCALES for now
    AVAILABLE_LANGUAGES = PANEL_LOCALES.filter((locale) => ['en', 'fr'].includes(locale.value));

    customerPrivacy$ = new BehaviorSubject('');

    private developmentFlags = DEVELOPMENT_FEATURE_FLAGS;
    gdprPrivacy$ = new BehaviorSubject('');

    userName$ = new BehaviorSubject(null);
    get userName(): string {
        return this.userName$.value;
    }

    organizationName$ = new BehaviorSubject(null);
    get organizationName(): string {
        return this.organizationName$.value;
    }

    canImpersonate$ = new BehaviorSubject(false);
    get canImpersonate(): boolean {
        return this.canImpersonate$.value;
    }

    isImpersonated$ = new BehaviorSubject(false);
    get isImpersonated(): boolean {
        return this.isImpersonated$.value;
    }

    impersonatedAs$ = new BehaviorSubject<ImpersonatedAs>(null);
    isSystemAdmin: boolean;
    isOrganizationAdmin: boolean;

    toTitleCase = toTitleCase;

    currentLanguage: string;

    gaDomain = environment.ga_domain;

    // Feature flag link visibility (only show for system admin)
    get showFeatureFlagLink(): boolean {
        return this.isSystemAdmin;
    }

    // Billing dashboard visibility (controlled by feature flag)
    showBillingDashboard$ = this.featureFlagService.isEnabled$('showBillingDashboard');

    // i18n texts
    onText = $localize`:Profile menu|Filter enabled:on`;
    offText = $localize`:Profile menu|Filter disabled:off`;

    constructor(
        private dialog: MatDialog,
        private auth: AuthService,
        private popup: PopupService,
        private cdRef: ChangeDetectorRef,
        private customerPrivacy: CustomerPrivacyService,
        // developmentService removed, using featureFlagService instead
        private router: Router,
        private gdprPrivacyService: GdprPrivacyService,
        private translate: TranslateService,
        private orgInfo: OrganizationInfoService,
        private localeService: LocaleService,
        private environmentService: EnvironmentService,
        private featureFlagService: FeatureFlagService
    ) {
        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((origin) => {
                this.themeClass = `theme-${origin}`;
                this.cdRef.markForCheck();
            });

        this.orgInfo.organizationInfo$.pipe(untilDestroyed(this)).subscribe({
            next: (info) => this.organizationName$.next(info?.impersonated?.tenantName || info?.originalTenantName),
        });
        this.auth.profile$.pipe(untilDestroyed(this)).subscribe({
            next: (profile) => {
                this.userName$.next(profile?.name || '');
                this.isImpersonated$.next(profile?.impersonated || false);
            },
        });

        combineLatest([this.auth.profile$, this.isImpersonated$])
            .pipe(untilDestroyed(this))
            .subscribe({
                next: ([profile, isImpersonated]) => {
                    if (!isImpersonated) {
                        this.impersonatedAs$.next(null);
                    } else {
                        const systemRoles = profile.systemRoles as AuthSystemRole[];
                        this.impersonatedAs$.next({
                            userRole: profile.userRole,
                            systemRoles: systemRoles,
                            systemRolesString: systemRoles
                                ? systemRoles.map((sr) => translateSystemRole(sr)).join(', ')
                                : undefined,
                        });
                    }
                },
            });

        combineLatest([this.auth.authenticated$, this.auth.profile$, this.orgInfo.userTenancyAccess$])
            .pipe(
                untilDestroyed(this),
                map(([authed, profile, userTenancyAccess]) => {
                    const isSystemAdmin =
                        (profile?.systemRoles || []).includes('systemAdmin') ||
                        (profile?.originalSystemRoles || []).includes('systemAdmin');
                    const hasTenancyAccess = userTenancyAccess?.length > 0;

                    return authed && (isSystemAdmin || hasTenancyAccess);
                })
            )
            .subscribe({
                next: (canImpersonate) => this.canImpersonate$.next(canImpersonate),
            });

        this.auth.authenticated$.subscribe(async (authed) => {
            if (authed) {
                this.isSystemAdmin = this.auth.getPermissions().isSystemAdmin;
                this.isOrganizationAdmin = this.auth.getPermissions().isOrganizationAdmin;
            } else {
                this.isOrganizationAdmin = false;
                this.isSystemAdmin = false;
            }
            this.cdRef.markForCheck();
        });

        this.customerPrivacy.enabled$.pipe(untilDestroyed(this)).subscribe({
            next: (enabled) => this.customerPrivacy$.next(enabled ? this.onText : this.offText),
        });

        this.gdprPrivacyService
            .gdprPrivacyEnabled$()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (enabled) => this.gdprPrivacy$.next(enabled ? this.onText : this.offText),
            });

        // Update selected language only when finish navigating
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                untilDestroyed(this)
            )
            .subscribe((_) => {
                const destinationLanguage = getCurrentLanguageFromPath();

                // set locale
                this.localeService.setLocale(destinationLanguage);

                // update language menu selection
                this.currentLanguage = destinationLanguage;

                this.cdRef.markForCheck();
            });
    }

    impersonate() {
        const canImpersonateWithoutSysAdmin = this.canImpersonate$.value && !this.isSystemAdmin;
        //const originalTenantName = this.organizationName;
        const dialogRef = ImpersonateDialogComponent.open(
            this.dialog,
            canImpersonateWithoutSysAdmin ? { userTenancyAccesses: this.orgInfo.userTenancyAccess } : null
        );
        dialogRef.afterClosed().subscribe((r) => {
            if (r && r.tenantId) {
                const popup = this.popup.open(`Switching to '${r.tenantName}'...`);
                this.auth
                    .impersonate({
                        tenantId: r.tenantId,
                        systemRoles: r.systemRoles,
                        userRole: r.userRole,
                        roles: r.roles,
                    })
                    .catch((e) => {
                        console.error(e);
                        const failed = this.translate.instant(
                            `common.dialogs.impersonate.${canImpersonateWithoutSysAdmin ? 'changing_organization_failed' : 'impersonating_failed'}`
                        );

                        this.popup.error(`${failed} ${get(e, 'error.errorMessage') || '-'}`);
                    })
                    .finally(() => {
                        this.orgInfo.setImpersonation(r.tenantId, r.tenantName);
                        popup.instance.data.icon = 'check_circle_outline';
                    });
            }
        });
    }

    backToMyOrganization() {
        const popup = this.popup.open(
            $localize`:Profile menu popup|:Returning to` + ` '${this.orgInfo.organizationInfo?.originalTenantName}'...`
        );
        const originalAuthInfo = this.auth.getOriginalAuthInfo();
        this.auth
            .impersonate({ tenantId: originalAuthInfo.originalTenantId, userRole: originalAuthInfo.originalUserRole })
            .catch((e) => {
                console.error(e);
                this.popup.error(
                    $localize`:Profile menu popup|:Failed to return to my organization: ${get(e, 'error.errorMessage') || '-'}`
                );
            })
            .finally(() => {
                this.orgInfo.removeImpersonation();
                popup.instance.data.icon = 'check_circle_outline';
            });
    }

    customerPrivacyFilterClick(event) {
        this.stopClickPropagate(event);
        this.customerPrivacy.toggle();
    }

    gdprPrivacyFilterClick(event) {
        this.stopClickPropagate(event);
        this.gdprPrivacyService.toggle();
    }

    modifyCookieConsent() {
        CookieDialogComponent.open(this.dialog);
    }

    openFeatureFlags() {
        // Navigate to feature flags page
        this.router.navigate(['/feature-flags']);
    }

    logout() {
        this.auth.logout();
    }

    stopClickPropagate(event) {
        event.stopPropagation();
    }

    triggerLanguageChange(locale: string) {
        const currentUrl = this.router.url;
        let url = window.location.origin;

        // English doesn't have the Language code in the URL for now
        if (locale !== 'en') {
            url += `/${locale}`;
        }
        // Append the currentUrl to origin
        url += currentUrl;

        window.open(url, '_self');
    }

    navigateToBillingDashboard(): void {
        this.auth.navigateToAdminIamOrganizationPage(this.auth.profile$.value.tenantId, 'BILLING');
    }

    localhostErrorPopup() {
        this.popup.error('This feature is not available on localhost', { durationMs: 1000 });
    }
}
